import { ComponentProps } from 'react';

import FrameTriggerClient from 'lib/clients/FrameTriggerClient';
import LivePhotoCaptureClient from 'lib/clients/LivePhotoCaptureClient';
import useInquiryFeatureFlag from 'lib/hooks/useInquiryFeatureFlag';
import { StepDocumentConfig } from 'lib/interfaces/StepsConfig';
import { calculateLuminosity } from 'verify/lib/utils/image';
import { bufferHorizontalImage } from 'verify/lib/utils/webcam';
import { DocumentFileOriginal } from 'verify/models/document-file';
import { CaptureMethod } from 'verify/models/verification';
import BaseScanner from 'verify/scanners/BaseScanner';
import {
  DETECT_INTERVAL,
  DOCUMENT_TRIGGER_CONFIGS,
  DocumentTriggerParams,
  DocumentTriggerType,
} from 'verify/scanners/DocumentScanner/client';

interface DocumentScannerProps {
  delayUntilManualCaptureMs?: number;
  handleCapture: (imageDataUris: string[], captureMethod: CaptureMethod) => Promise<void>;
  handleError: (err: Error | string, state: 'load' | 'capture') => void;
  labelTakePhoto: string;
  videoConstraints: MediaTrackConstraints;
  captures?: DocumentFileOriginal[];
  renderButton?: ComponentProps<typeof BaseScanner>['renderButton'];
}

export default function DocumentScanner({
  /**
   * Manual capture was always enabled for DocumentIdScanner before this prop was implemented.
   */
  delayUntilManualCaptureMs = 0,
  handleCapture,
  handleError,
  labelTakePhoto,
  videoConstraints,
  captures,
  renderButton,
}: DocumentScannerProps) {
  const enableLowLightDetection = useInquiryFeatureFlag('inquiry:enable-low-light-detection-web');

  const paramsFromVideoFrame = async (
    videoElement: HTMLVideoElement,
    canvasBuffer: HTMLCanvasElement,
    _captureClient: LivePhotoCaptureClient,
    _triggerClient: FrameTriggerClient<
      DocumentTriggerType,
      DocumentTriggerParams,
      StepDocumentConfig['localizations']['capture-page']
    >,
  ): Promise<DocumentTriggerParams> => {
    bufferHorizontalImage({ source: videoElement, precreatedCanvas: canvasBuffer });

    try {
      return {
        luminosityData: enableLowLightDetection ? calculateLuminosity(canvasBuffer) : null,
      };
    } catch (err) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleError(err, 'capture');
    }

    // Currently have no triggers besides a default message.
    return {
      luminosityData: null,
    };
  };
  const shouldSubmit = (
    _params: DocumentTriggerParams,
    _triggerClient: FrameTriggerClient<
      DocumentTriggerType,
      DocumentTriggerParams,
      StepDocumentConfig['localizations']['capture-page']
    >,
  ) => {
    // One day we will have auto capture for docs, maybe with four corners check!
    return false;
  };

  return (
    <BaseScanner<
      DocumentTriggerType,
      DocumentTriggerParams,
      StepDocumentConfig['localizations']['capture-page']
    >
      delayUntilManualCaptureMs={delayUntilManualCaptureMs}
      handleCapture={({ imageSrcs, captureMethod }) => handleCapture(imageSrcs, captureMethod)}
      handleFailed={handleError}
      detectInterval={DETECT_INTERVAL}
      labelTakePhoto={labelTakePhoto}
      videoConstraints={videoConstraints}
      triggerConfigs={DOCUMENT_TRIGGER_CONFIGS}
      paramsFromVideoFrame={paramsFromVideoFrame}
      shouldSubmit={shouldSubmit}
      captures={captures}
      scannerType='document'
      renderButton={renderButton}
      zoomFactor={1}
    />
  );
}
